/**
 * Download content as a file
 * @param {string} filename - The name of the file
 * @param {string} content - The content to download
 * @param {string} mimeType - The MIME type of the file
 */
export const downloadFile = (filename, content, mimeType = 'text/plain') => {
  try {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    URL.revokeObjectURL(url)
  } catch (error) {
    throw new Error(`Failed to download file: ${error.message}`)
  }
}

/**
 * Copy text to clipboard
 * @param {string} text - The text to copy
 * @returns {Promise<boolean>} - Whether the copy was successful
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      
      return successful
    }
  } catch (error) {
    throw new Error(`Failed to copy to clipboard: ${error.message}`)
  }
}

/**
 * Read file content
 * @param {File} file - The file to read
 * @returns {Promise<string>} - The file content
 */
export const readFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (event) => {
      resolve(event.target.result)
    }
    
    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.message}`))
    }
    
    reader.readAsText(file)
  })
}

/**
 * Get file size in human readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} - Human readable size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Validate file type
 * @param {File} file - The file to validate
 * @param {Array} allowedTypes - Array of allowed MIME types
 * @returns {boolean} - Whether the file type is allowed
 */
export const validateFileType = (file, allowedTypes = ['application/json', 'text/plain']) => {
  return allowedTypes.includes(file.type) || 
         file.name.toLowerCase().endsWith('.json') ||
         file.name.toLowerCase().endsWith('.txt')
}

/**
 * Generate unique filename
 * @param {string} baseName - Base name for the file
 * @param {string} extension - File extension
 * @returns {string} - Unique filename with timestamp
 */
export const generateUniqueFilename = (baseName = 'json-data', extension = 'json') => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `${baseName}-${timestamp}.${extension}`
}

/**
 * Export JSON data in different formats
 * @param {Object} data - The data to export
 * @param {string} format - Export format (json, yaml, csv)
 * @returns {string} - Formatted data string
 */
export const exportData = (data, format = 'json') => {
  switch (format.toLowerCase()) {
    case 'json':
      return JSON.stringify(data, null, 2)
    
    case 'yaml':
      try {
        const yaml = require('js-yaml')
        return yaml.dump(data, { indent: 2 })
      } catch (error) {
        throw new Error('YAML export requires js-yaml package')
      }
    
    case 'csv':
      return convertToCSV(data)
    
    default:
      throw new Error(`Unsupported export format: ${format}`)
  }
}

/**
 * Convert JSON to CSV (simple implementation)
 * @param {Object} data - The data to convert
 * @returns {string} - CSV string
 */
const convertToCSV = (data) => {
  if (Array.isArray(data)) {
    if (data.length === 0) return ''
    
    const headers = Object.keys(data[0])
    const csvRows = [headers.join(',')]
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header]
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
      })
      csvRows.push(values.join(','))
    })
    
    return csvRows.join('\n')
  } else {
    throw new Error('CSV export only supports array data')
  }
}
